import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { Link, useNavigate } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import '../login/login.scss'
import logo from 'src/assets/images/1journey.png'
import art from 'src/assets/images/readingbook.png'

const LockIcon = () => (
  <svg
    className="jl__lead"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="4" y="10.5" width="16" height="10" rx="2.5" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
  </svg>
)
const EyeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)
const EyeOffIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9.9 4.2A9.6 9.6 0 0 1 12 4c6.5 0 10 7 10 7a15.8 15.8 0 0 1-3 3.7M6.1 6.1A15.8 15.8 0 0 0 2 11s3.5 7 10 7a9.6 9.6 0 0 0 3.5-.6M3 3l18 18M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
)
const ArrowIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)

const SetPassword = () => {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [token, setToken] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    setToken(searchParams.get('token'))
  }, [])

  const validate = () => {
    const next = {}
    if (!password) next.password = 'Please enter your new password'
    else if (password.length < 8) next.password = 'Password must be at least 8 characters long'
    if (!confirmPassword) next.confirmPassword = 'Please confirm your new password'
    else if (password !== confirmPassword) next.confirmPassword = 'Passwords do not match'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setIsLoading(true)
    try {
      await axios.post(`${process.env.REACT_APP_BASE_URL_API}/Account/Set-Password`, {
        token,
        password,
      })
      toast.success('Password set successfully', {
        autoClose: 4000,
        style: { background: 'lightgreen', color: 'black' },
      })
      localStorage.clear()
      navigate('/login', {
        state: { successMessage: 'Password set successfully. Please log in.' },
      })
    } catch (error) {
      // Batch-07 set-password error codes.
      const body = error?.response?.rawData || error?.response?.data
      const code = body?.code
      const map = {
        PASSWORD_SETUP_TOKEN_INVALID:
          'This link is invalid or has already been used. Please request a new one.',
        PASSWORD_SETUP_TOKEN_EXPIRED:
          'This link has expired. Please request a new set-password link.',
        PASSWORD_SETUP_FAILED: 'Could not set your password. Please try again.',
      }
      const friendly =
        map[code] ||
        (body && body.message) ||
        (typeof body === 'string' ? body : 'Something went wrong. Please try again.')
      toast.error(friendly, {
        autoClose: 5000,
        style: { background: 'lightcoral', color: 'white' },
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="jl">
      <ToastContainer />
      <div className="jl__shell">
        <aside className="jl__hero jl__hero--art">
          <div className="jl__aurora" aria-hidden="true">
            <span className="jl__orb jl__orb--a" />
            <span className="jl__orb jl__orb--b" />
            <span className="jl__orb jl__orb--c" />
            <span className="jl__beam" />
            <span className="jl__grid" />
          </div>
          <div className="jl__art">
            <span className="jl__art-glow" aria-hidden="true" />
            <img src={art} alt="A student reading" className="jl__art-img" />
            <div className="jl__art-tag">
              <span>1Journey</span>
              <strong>Stay with us</strong>
            </div>
          </div>
        </aside>

        <main className="jl__panel">
          <div className="jl__card">
            <img src={logo} alt="1Journey" className="jl__card-logo" />
            <span className="jl__eyebrow">Set password</span>
            <h2 className="jl__title">Set your password</h2>
            <p className="jl__sub">Create a password to finish setting up your account.</p>

            <form className="jl__form" onSubmit={handleSubmit} noValidate>
              <div className="jl__field">
                <label htmlFor="sp-pw">New password</label>
                <div className="jl__control">
                  <LockIcon />
                  <input
                    id="sp-pw"
                    type={showPw ? 'text' : 'password'}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="jl__eye"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >
                    {showPw ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {errors.password && <span className="jl__err">{errors.password}</span>}
              </div>

              <div className="jl__field">
                <label htmlFor="sp-confirm">Confirm new password</label>
                <div className="jl__control">
                  <LockIcon />
                  <input
                    id="sp-confirm"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="jl__eye"
                    onClick={() => setShowConfirm((v) => !v)}
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  >
                    {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <span className="jl__err">{errors.confirmPassword}</span>
                )}
              </div>

              <button type="submit" className="jl__btn" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="jl__spinner" aria-hidden="true" /> Setting&hellip;
                  </>
                ) : (
                  <>
                    Set password <ArrowIcon />
                  </>
                )}
              </button>
            </form>

            <p className="jl__foot">
              Already set? <Link to="/login">Back to log in</Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  )
}

export default SetPassword
